<template>
  <div class="ai-assistant" v-if="enabled">
    <portal to="medical-record-assistant">
      <button
        type="button"
        class="ai-assistant-trigger"
        :class="{ discover: !discovered, active: open }"
        aria-label="Assistente IA"
        :aria-expanded="String(open)"
        @click="toggle"
      >
        <fa-icon :icon="['fal', 'sparkles']" />
        <span>Assistente IA</span>
      </button>
    </portal>

    <aside class="ai-assistant-panel" :class="{ open }">
      <div class="ai-assistant-header">
        <div class="ai-assistant-header-title">
          <fa-icon :icon="['fal', 'sparkles']" />
          <div>
            <h6>Assistente do atendimento</h6>
            <span>Respostas por IA — revise antes de usar</span>
          </div>
        </div>
        <div class="ai-assistant-header-actions">
          <button
            type="button"
            class="btn btn-action btn-icon tooltip tooltip-left"
            data-tooltip="Limpar conversa"
            v-if="messages.length > 0"
            @click="confirmClear"
          ><fa-icon :icon="['fal', 'redo']" /></button>
          <button type="button" class="btn btn-action btn-icon" @click="close">
            <fa-icon :icon="['fal', 'times']" />
          </button>
        </div>
      </div>

      <div class="ai-assistant-tools" v-if="hasNoaNotes">
        <button type="button" class="ai-assistant-tool" @click="openTool('noa-notes')">
          <span class="ai-assistant-tool-icon">
            <!-- eslint-disable max-len -->
            <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <g>
                <path d="M45.6,18.7,41,14.9V7.5a1,1,0,0,0-.6-.9L30.5,2.1h-.4l-.6.2L24,5.9,18.5,2.2,17.9,2h-.4L7.6,6.6a1,1,0,0,0-.6.9v7.4L2.4,18.7a.8.8,0,0,0-.4.8v9H2a.8.8,0,0,0,.4.8L7,33.1v7.4a1,1,0,0,0,.6.9l9.9,4.5h.4l.6-.2L24,42.1l5.5,3.7.6.2h.4l9.9-4.5a1,1,0,0,0,.6-.9V33.1l4.6-3.8a.8.8,0,0,0,.4-.7V19.4h0A.8.8,0,0,0,45.6,18.7Zm-5.1,6.8H42v1.6l-3.5,2.8-.4.3-.4-.2a1.4,1.4,0,0,0-2,.7,1.5,1.5,0,0,0,.6,2l.7.3h0v5.4l-6.6,3.1-4.2-2.8-.7-.5V25.5H27a1.5,1.5,0,0,0,0-3H25.5V9.7l.7-.5,4.2-2.8L37,9.5v5.4h0l-.7.3a1.5,1.5,0,0,0-.6,2,1.4,1.4,0,0,0,1.3.9l.7-.2.4-.2.4.3L42,20.9v1.6H40.5a1.5,1.5,0,0,0,0,3ZM21,25.5h1.5V38.3l-.7.5-4.2,2.8L11,38.5V33.1h0l.7-.3a1.5,1.5,0,0,0,.6-2,1.4,1.4,0,0,0-2-.7l-.4.2-.4-.3L6,27.1V25.5H7.5a1.5,1.5,0,0,0,0-3H6V20.9l3.5-2.8.4-.3.4.2.7.2a1.4,1.4,0,0,0,1.3-.9,1.5,1.5,0,0,0-.6-2L11,15h0V9.5l6.6-3.1,4.2,2.8.7.5V22.5H21a1.5,1.5,0,0,0,0,3Z"/>
                <path d="M13.9,9.9a1.8,1.8,0,0,0,0,2.2l2.6,2.5v2.8l-4,4v5.2l4,4v2.8l-2.6,2.5a1.8,1.8,0,0,0,0,2.2,1.5,1.5,0,0,0,1.1.4,1.5,1.5,0,0,0,1.1-.4l3.4-3.5V29.4l-4-4V22.6l4-4V13.4L16.1,9.9A1.8,1.8,0,0,0,13.9,9.9Z"/>
                <path d="M31.5,14.6l2.6-2.5a1.8,1.8,0,0,0,0-2.2,1.8,1.8,0,0,0-2.2,0l-3.4,3.5v5.2l4,4v2.8l-4,4v5.2l3.4,3.5a1.7,1.7,0,0,0,2.2,0,1.8,1.8,0,0,0,0-2.2l-2.6-2.5V30.6l4-4V21.4l-4-4Z"/>
              </g>
            </svg>
            <!-- eslint-enable max-len -->
          </span>
          <span class="ai-assistant-tool-info">
            <strong>Noa Notes</strong>
            <small>Transcrição e resumo da consulta</small>
          </span>
          <fa-icon :icon="['fal', 'chevron-right']" />
        </button>
      </div>

      <div class="ai-assistant-body" ref="body">
        <div class="ai-assistant-intro" v-if="messages.length === 0 && !sending">
          <p>
            Pergunte sobre o histórico, medicações, alergias, hipóteses diagnósticas
            ou condutas deste atendimento.
          </p>
          <p class="ai-assistant-scope" v-if="scopeLabel">{{ scopeLabel }}</p>
          <button
            type="button"
            class="ai-assistant-suggestion"
            v-for="(suggestion, i) in tabSuggestions"
            :key="i"
            :disabled="sending || exhausted"
            @click="ask(suggestion)"
          >
            <span>{{ suggestion }}</span>
            <fa-icon :icon="['fal', 'chevron-right']" />
          </button>
        </div>

        <div
          class="ai-assistant-message"
          :class="message.role"
          v-for="(message, i) in messages"
          :key="i"
        >
          <div class="ai-assistant-message-content">{{ message.content }}</div>

          <div class="ai-assistant-citations" v-if="hasItems(message.citations)">
            <span class="ai-assistant-citations-label">Baseado em</span>
            <span
              class="ai-assistant-citation"
              v-for="(citation, c) in message.citations"
              :key="c"
            >{{ citation.date | date }}</span>
          </div>

          <div class="ai-assistant-actions" v-if="hasItems(message.actions)">
            <template v-for="(action, a) in message.actions">
              <div class="ai-assistant-document" v-if="action.type === 'create_document'" :key="a">
                <strong v-if="action.title">{{ action.title }}</strong>
                <div class="ai-assistant-document-text">{{ action.text }}</div>
              </div>
              <button
                type="button"
                class="ai-assistant-action"
                :key="`action-${a}`"
                @click="runAction(action)"
              >
                <fa-icon :icon="['fal', 'plus-circle']" />
                <span>{{ actionLabel(action) }}</span>
              </button>
            </template>
          </div>
        </div>

        <div class="ai-assistant-message assistant typing" v-if="sending">
          <span /><span /><span />
        </div>

        <div class="ai-assistant-error" v-if="error">
          <fa-icon :icon="['fal', 'exclamation-triangle']" />
          <span>{{ error }}</span>
          <button
            type="button"
            class="btn btn-sm btn-link"
            v-if="!unavailable"
            @click="retry"
          >Tentar novamente</button>
        </div>
      </div>

      <div class="ai-assistant-footer">
        <template v-if="messages.length > 0 && tabSuggestions.length > 0">
          <div class="ai-assistant-hints" v-if="showHints && !exhausted">
            <button
              type="button"
              class="ai-assistant-suggestion"
              v-for="(suggestion, i) in tabSuggestions"
              :key="i"
              :disabled="sending || exhausted"
              @click="askHint(suggestion)"
            >
              <span>{{ suggestion }}</span>
              <fa-icon :icon="['fal', 'chevron-right']" />
            </button>
          </div>
          <button
            type="button"
            class="ai-assistant-hints-toggle"
            :class="{ open: showHints }"
            :disabled="exhausted"
            @click="showHints = !showHints"
          >
            <fa-icon :icon="['fal', 'lightbulb']" />
            <span>{{ hintsLabel }}</span>
            <fa-icon :icon="['fal', showHints ? 'chevron-down' : 'chevron-up']" />
          </button>
        </template>
        <div class="ai-assistant-input">
          <textarea
            class="form-input"
            rows="3"
            ref="question"
            v-model="question"
            :disabled="sending || exhausted"
            :placeholder="exhausted
              ? 'Limite de perguntas de hoje atingido'
              : 'Digite sua dúvida...'"
            @keydown.enter.exact.prevent="ask(question)"
          />
          <button
            type="button"
            class="btn btn-icon ai-assistant-send"
            :class="{ active: hasQuestion }"
            :disabled="!hasQuestion || sending || exhausted"
            @click="ask(question)"
          ><fa-icon :icon="['fal', 'paper-plane']" /></button>
        </div>
      </div>

      <div class="ai-assistant-plan" :class="{ exhausted }">
        <span class="ai-assistant-plan-info">
          <span class="ai-assistant-plan-name">{{ planName }}</span>
          <span class="ai-assistant-plan-detail">{{ planDetail }}</span>
        </span>
        <span class="ai-assistant-plan-bar" v-if="quota.enabled">
          <span :style="{ width: `${quotaPercent}%` }" />
        </span>
      </div>
    </aside>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ai from '@/mixins/ai';

const DISCOVERED_KEY = 'stenci.medicalRecordAssistant.discovered';

const LOAD_ATTEMPTS = 3;
const LOAD_RETRY_DELAY = 4000;

const TAB_LABELS = {
  'tab-anamnesis': 'anamnese',
  'tab-request': 'solicitação de exames',
  'tab-prescriptions': 'prescrição',
  'tab-recipes': 'receituário',
};

export default {
  mixins: [ai],
  props: {
    medicalRecordId: {
      type: String,
      default: '',
    },
    draft: {
      type: Object,
      default: null,
    },
    specialty: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      open: false,
      enabled: false,
      sending: false,
      error: '',
      unavailable: false,
      discovered: true,
      question: '',
      pending: '',
      showHints: false,
      suggestions: {},
      messages: [],
      loadTimeout: null,
      history: { limit: 0, total: 0 },
      quota: {
        enabled: false,
        free: true,
        used: 0,
        limit: 0,
        remaining: 0,
      },
    };
  },
  mounted() {
    this.discovered = this.isDiscovered();
    this.loadContext();
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    clearTimeout(this.loadTimeout);
    document.removeEventListener('keydown', this.onKeydown);
  },
  watch: {
    medicalRecordId() {
      this.messages = [];
      this.error = '';
      this.unavailable = false;
      this.loadContext();
    },
  },
  computed: {
    ...mapGetters({
      branchSettings: 'branchSettings',
    }),
    // o Noa Notes é contratado por filial: sem ele ligado, a ferramenta não aparece
    hasNoaNotes() {
      return !!this.branchSettings && this.branchSettings['noaNotes.enabled'] === true;
    },
    hasQuestion() {
      return !!this.question.trim();
    },
    tabSuggestions() {
      const tab = this.draft && this.draft.tab;

      return (tab && this.suggestions[tab]) || this.suggestions.default || [];
    },
    scopeLabel() {
      const { limit, total } = this.history;

      if (!total) {
        return '';
      }

      if (total <= limit) {
        return total === 1
          ? 'Considera o atendimento anterior e o atual.'
          : `Considera os ${total} atendimentos anteriores e o atual.`;
      }

      return `Considera os ${limit} atendimentos mais recentes, de ${total} registrados.`;
    },
    exhausted() {
      return this.quota.enabled && this.quota.remaining === 0;
    },
    planName() {
      return this.quota.free ? 'Plano Grátis' : 'Plano contratado';
    },
    planDetail() {
      if (!this.quota.enabled) {
        return this.quota.free ? 'uso limitado' : 'uso incluído no plano';
      }

      if (this.exhausted) {
        return 'limite de perguntas de hoje atingido';
      }

      return this.quota.remaining === 1
        ? 'resta 1 pergunta hoje'
        : `restam ${this.quota.remaining} perguntas hoje`;
    },
    quotaPercent() {
      if (!this.quota.limit) {
        return 0;
      }

      return Math.min(Math.round((this.quota.used / this.quota.limit) * 100), 100);
    },
    hintsLabel() {
      const tab = this.draft && this.draft.tab;
      const context = tab && this.suggestions[tab] ? TAB_LABELS[tab] : '';

      return context
        ? `Sugestões de pergunta para ${context}`
        : 'Sugestões de pergunta';
    },
  },
  methods: {
    storage() {
      try {
        return window.localStorage;
      } catch (e) {
        return null;
      }
    },
    isDiscovered() {
      const storage = this.storage();

      return storage ? !!storage.getItem(DISCOVERED_KEY) : true;
    },
    setDiscovered() {
      const storage = this.storage();

      this.discovered = true;

      if (storage) {
        storage.setItem(DISCOVERED_KEY, '1');
      }
    },
    hasItems(value) {
      return Array.isArray(value) && value.length > 0;
    },
    actionLabel(action) {
      if (action.type === 'add_cid') {
        return `Adicionar CID ${action.code}`;
      }

      if (action.type === 'search_exam') {
        return action.label || `Buscar ${action.term}`;
      }

      return action.label || 'Adicionar à anamnese';
    },
    loadContext(attempt = 1) {
      clearTimeout(this.loadTimeout);

      if (!this.medicalRecordId || !this.aiEnabled) {
        this.enabled = false;
        return Promise.resolve();
      }

      return this.$httpX
        .get(`/medical-records/${this.medicalRecordId}/assistant`)
        .then(({ data }) => {
          this.enabled = !!data.enabled;
          this.suggestions = data.suggestions || {};
          this.messages = data.messages || [];
          this.quota = data.quota || this.quota;
          this.history = data.history || this.history;
        })
        .catch((e) => {
          const status = e.response && e.response.status;

          if (status && status < 500) {
            this.enabled = false;
            return;
          }

          if (attempt < LOAD_ATTEMPTS) {
            this.loadTimeout = setTimeout(() => this.loadContext(attempt + 1), LOAD_RETRY_DELAY);
          }
        });
    },
    toggle() {
      this.open = !this.open;

      if (this.open) {
        this.setDiscovered();
        this.scrollToEnd();
        this.$nextTick(() => {
          if (this.$refs.question) {
            this.$refs.question.focus();
          }
        });
      }
    },
    close() {
      this.open = false;
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        this.close();
      }
    },
    ask(content) {
      const text = (content || '').trim();

      if (!text || this.sending || this.exhausted) {
        return Promise.resolve();
      }

      this.question = '';
      this.pending = text;
      this.messages.push({ role: 'user', content: text });

      return this.send();
    },
    retry() {
      return this.send();
    },
    send() {
      if (!this.pending) {
        return Promise.resolve();
      }

      this.error = '';
      this.sending = true;
      this.scrollToEnd();

      return this.$httpX
        .post(`/medical-records/${this.medicalRecordId}/assistant/messages`, {
          question: this.pending,
          draft: this.draft,
          specialty: this.specialty,
        })
        .then(({ data }) => {
          this.messages.push(data);
          this.quota = data.quota || this.quota;
          this.pending = '';
        })
        .catch((e) => {
          const response = e.response || {};

          this.unavailable = [402, 503].includes(response.status);

          if (response.data && response.data.quota) {
            this.quota = response.data.quota;
          }
          this.error = (response.data && response.data.message)
            || 'Não foi possível responder agora.';
        })
        .finally(() => {
          this.sending = false;
          this.scrollToEnd();
        });
    },
    confirmClear() {
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Limpar conversa</h5>'
          + '<div>As perguntas e respostas deste atendimento serão apagadas.</div>'
          + '<div>Deseja continuar?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.clear();
              close();
            },
          },
        ],
      });
    },
    clear() {
      return this.$httpX
        .delete(`/medical-records/${this.medicalRecordId}/assistant/messages`)
        .then(() => {
          this.messages = [];
          this.pending = '';
          this.error = '';
        })
        .catch(this.$toast.error);
    },
    askHint(suggestion) {
      this.showHints = false;

      return this.ask(suggestion);
    },
    runAction(action) {
      this.$emit('action', action);
    },
    openTool(tool) {
      this.close();
      this.$emit('tool', tool);
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const { body } = this.$refs;

        if (body) {
          body.scrollTop = body.scrollHeight;
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/_mixins.scss";

$ai-assistant-top: 72px;
$ai-assistant-width: 22rem;

.ai-assistant-trigger {
  align-items: center;
  background-color: lighten($primary-color, 34%);
  border: $border-width solid lighten($primary-color, 26%);
  border-radius: 1rem;
  color: $primary-color;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: $font-size;
  font-weight: 600;
  gap: $layout-spacing;
  height: $control-size-lg;
  justify-content: center;
  line-height: 1;
  padding: 0 $layout-spacing-lg;
  transition: background-color .2s ease, border-color .2s ease, color .2s ease;
  white-space: nowrap;

  svg {
    font-size: $font-size-lg;
  }

  &:hover {
    background-color: lighten($primary-color, 32%);
    border-color: lighten($primary-color, 24%);
  }

  &:focus {
    outline: 0;
  }

  &:focus-visible {
    @include control-shadow();
  }

  &.active {
    background-color: $primary-color;
    border-color: $primary-color;
    color: $light-color;

    &:hover {
      background-color: $primary-color-dark;
      border-color: $primary-color-dark;
    }
  }

  &.discover {
    animation: ai-assistant-attention 2.4s ease-out infinite;
  }

  @media screen and (max-width: $size-md) {
    padding: 0;
    width: $control-size-lg;

    span {
      display: none;
    }
  }
}

.ai-assistant {
  .ai-assistant-panel {
    background: $light-color;
    border-left: $border-width solid $border-color;
    bottom: 0;
    box-shadow: -4px 0 24px rgba($gray-color-900, .12);
    display: flex;
    flex-direction: column;
    position: fixed;
    right: 0;
    top: $ai-assistant-top;
    transform: translateX(100%);
    transition: transform .25s ease;
    visibility: hidden;
    width: $ai-assistant-width;
    z-index: $zindex-2;

    &.open {
      transform: translateX(0);
      visibility: visible;
    }
  }

  .ai-assistant-header {
    align-items: center;
    background: linear-gradient(135deg, $purple-neon-color-500, $purple-neon-color-400);
    color: $light-color;
    display: flex;
    justify-content: space-between;
    padding: $layout-spacing-lg;

    .ai-assistant-header-title {
      align-items: center;
      display: flex;
      gap: $layout-spacing-lg;

      h6 {
        font-size: $font-size-lg;
        font-weight: 600;
        margin: 0;
      }

      span {
        font-size: $font-size-sm;
        opacity: .8;
      }
    }

    .ai-assistant-header-actions {
      display: flex;
      flex-shrink: 0;
      gap: $layout-spacing-sm;
    }

    .btn {
      background: transparent;
      border: 0;
      color: $light-color;

      &:active,
      &:focus,
      &:hover {
        background: rgba($light-color, .2);
        box-shadow: none;
        color: $light-color;
      }
    }
  }

  .ai-assistant-tools {
    border-bottom: $border-width solid $border-color;
    flex-shrink: 0;

    .ai-assistant-tool {
      align-items: center;
      background: transparent;
      border: 0;
      cursor: pointer;
      display: flex;
      gap: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing-lg;
      text-align: left;
      transition: background-color .2s ease;
      width: 100%;

      &:hover {
        background: $bg-color;
      }

      .ai-assistant-tool-icon {
        align-items: center;
        background: $purple-neon-color-100;
        border-radius: $border-radius;
        display: flex;
        flex-shrink: 0;
        height: 1.4rem;
        justify-content: center;
        width: 1.4rem;

        svg {
          fill: $purple-neon-color-500;
          height: .9rem;
          width: .9rem;
        }
      }

      .ai-assistant-tool-info {
        flex: 1;

        strong {
          color: $gray-color-900;
          display: block;
          font-size: $font-size;
        }

        small {
          color: $gray-color-700;
          font-size: $font-size-sm;
        }
      }

      svg {
        color: $gray-color-600;
        flex-shrink: 0;
      }
    }
  }

  .ai-assistant-body {
    @include scroll-bar();
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $layout-spacing-lg;
    overflow-y: auto;
    padding: $layout-spacing-lg;
  }

  .ai-assistant-intro {
    display: flex;
    flex-direction: column;
    gap: $layout-spacing;

    p {
      color: $gray-color-700;
      font-size: $font-size-sm;
      margin: 0 0 $layout-spacing 0;
    }

    .ai-assistant-scope {
      color: $gray-color-600;
      font-size: $font-size-xs;
    }
  }

  .ai-assistant-suggestion {
    align-items: center;
    background: rgba($purple-neon-color-300, .12);
    border: $border-width solid $purple-neon-color-300;
    border-radius: $unit-3;
    color: $purple-neon-color-700;
    cursor: pointer;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
    text-align: left;
    transition: border-color .2s ease, background-color .2s ease;
    width: 100%;

    &:hover:not(:disabled) {
      background: rgba($purple-neon-color-300, .28);
      border-color: $purple-neon-color-400;
    }

    span {
      flex: 1;
      min-width: 0;
    }

    svg {
      color: $purple-neon-color-500;
      flex-shrink: 0;
    }
  }

  .ai-assistant-message {
    border-radius: $unit-3;
    font-size: $font-size-sm;
    max-width: 90%;
    padding: $layout-spacing $layout-spacing-lg;

    .ai-assistant-message-content {
      white-space: pre-line;
    }

    &.user {
      align-self: flex-end;
      background: rgba($primary-color, .03);
      border: $border-width solid $purple-neon-color-200;
      color: $purple-neon-color-900;
    }

    &.assistant {
      align-self: flex-start;
      background: $gray-color-100;
      border: $border-width solid $border-color;
      color: $gray-color-900;
    }

    &.typing {
      display: flex;
      gap: $layout-spacing-sm;

      span {
        animation: ai-assistant-typing 1.2s ease-in-out infinite;
        background: $purple-neon-color-400;
        border-radius: 50%;
        height: .25rem;
        width: .25rem;

        &:nth-child(2) {
          animation-delay: .2s;
        }

        &:nth-child(3) {
          animation-delay: .4s;
        }
      }
    }
  }

  .ai-assistant-citations {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    margin-top: $layout-spacing;

    .ai-assistant-citations-label {
      color: $gray-color-600;
      font-size: $font-size-xs;
    }

    .ai-assistant-citation {
      background: $light-color;
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      color: $gray-color-700;
      font-size: $font-size-xs;
      padding: 0 $layout-spacing-sm;
    }
  }

  .ai-assistant-actions {
    display: flex;
    flex-direction: column;
    gap: $layout-spacing-sm;
    margin-top: $layout-spacing;

    .ai-assistant-document {
      background: $light-color;
      border: $border-width solid $border-color;
      border-radius: $unit-3;
      padding: $layout-spacing $layout-spacing-lg;

      strong {
        color: $gray-color-900;
        display: block;
        margin-bottom: $layout-spacing-sm;
      }

      .ai-assistant-document-text {
        color: $gray-color-800;
        font-size: $font-size-sm;
        max-height: 12rem;
        overflow-y: auto;
        white-space: pre-line;
      }
    }

    .ai-assistant-action {
      align-items: center;
      background: rgba($purple-neon-color-300, .12);
      border: $border-width solid $purple-neon-color-300;
      border-radius: $unit-3;
      color: $purple-neon-color-700;
      cursor: pointer;
      display: flex;
      font-size: $font-size-sm;
      font-weight: 600;
      gap: $layout-spacing;
      padding: $layout-spacing-sm $layout-spacing-lg;
      text-align: left;
      transition: border-color .2s ease, background-color .2s ease;

      &:hover {
        background: rgba($purple-neon-color-300, .28);
        border-color: $purple-neon-color-400;
      }

      span {
        flex: 1;
        min-width: 0;
      }

      svg {
        flex-shrink: 0;
      }
    }
  }

  .ai-assistant-error {
    align-items: center;
    color: $error-color;
    display: flex;
    font-size: $font-size-sm;
    gap: $layout-spacing;

    .btn {
      height: auto;
      padding: 0;
    }
  }

  .ai-assistant-plan {
    align-items: center;
    background: $gray-color-100;
    border: 0;
    border-top: $border-width solid $border-color;
    display: flex;
    gap: $layout-spacing-lg;
    padding: $layout-spacing $layout-spacing-lg;
    text-align: left;
    width: 100%;

    .ai-assistant-plan-info {
      align-items: baseline;
      display: flex;
      flex: 1;
      gap: $layout-spacing-sm;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
    }

    .ai-assistant-plan-name {
      color: $gray-color-800;
      flex-shrink: 0;
      font-size: $font-size-sm;
      font-weight: 600;
    }

    .ai-assistant-plan-detail {
      color: $gray-color-600;
      font-size: $font-size-xs;
      overflow: hidden;
      text-overflow: ellipsis;

      &::before {
        content: "·";
        margin-right: $layout-spacing-sm;
      }
    }

    .ai-assistant-plan-bar {
      background: $gray-color-200;
      border-radius: $unit-1;
      display: block;
      flex-shrink: 0;
      height: .15rem;
      overflow: hidden;
      width: 4rem;

      span {
        background: $purple-neon-color-400;
        display: block;
        height: 100%;
        transition: width .3s ease;
      }
    }

    &.exhausted {
      background: rgba($error-color, .08);
      border-top-color: $error-color;

      .ai-assistant-plan-name {
        color: $error-color;
      }

      .ai-assistant-plan-bar span {
        background: $error-color;
      }
    }
  }

  .ai-assistant-footer {
    border-top: $border-width solid $border-color;
    padding: $layout-spacing-lg;

    .ai-assistant-hints {
      display: flex;
      flex-direction: column;
      gap: $layout-spacing;
      margin-bottom: $layout-spacing;
    }

    .ai-assistant-hints-toggle {
      align-items: center;
      background: transparent;
      border: 0;
      color: $purple-neon-color-700;
      cursor: pointer;
      display: flex;
      font-size: $font-size-sm;
      gap: $layout-spacing;
      margin-bottom: $layout-spacing;
      padding: $layout-spacing-sm 0;
      transition: color .2s ease;
      width: 100%;

      &:hover:not(:disabled) {
        color: $purple-neon-color-500;
      }

      &:disabled {
        color: $gray-color-600;
        cursor: default;

        svg {
          color: $gray-color-600;
        }
      }

      span {
        flex: 1;
        min-width: 0;
        text-align: left;
      }

      svg {
        color: $purple-neon-color-500;
        flex-shrink: 0;
      }

      svg:last-child {
        border-radius: 50%;
        box-sizing: content-box;
        font-size: .8em;
        padding: $layout-spacing-sm;
        transition: background-color .2s ease;
      }

      &.open svg:last-child {
        background: rgba($purple-neon-color-300, .22);
        color: $purple-neon-color-600;
      }

      &.open:hover svg:last-child {
        background: rgba($purple-neon-color-300, .4);
      }
    }

    .ai-assistant-input {
      position: relative;

      .form-input {
        max-height: 9rem;
        min-height: 3.6rem;
        padding-right: 1.6rem;
        resize: none;

        &:focus {
          border-color: $purple-neon-color-400;
        }
      }

      .ai-assistant-send {
        background: transparent;
        border: 0;
        bottom: $layout-spacing-sm;
        color: $gray-color-light;
        padding: $layout-spacing-sm;
        position: absolute;
        right: $layout-spacing-sm;
        transition: color .15s ease;

        &:focus {
          box-shadow: none;
        }

        &.active {
          color: $purple-neon-color-500;
        }
      }
    }

  }
}

@keyframes ai-assistant-attention {
  0% {
    box-shadow: 0 0 0 0 rgba($primary-color, .35);
  }

  70%,
  100% {
    box-shadow: 0 0 0 .3rem rgba($primary-color, 0);
  }
}

@keyframes ai-assistant-typing {
  0%,
  100% {
    opacity: .3;
    transform: translateY(0);
  }

  50% {
    opacity: 1;
    transform: translateY(-.1rem);
  }
}
</style>
